import { apiService, ApiResponse } from './api';
import { API_ENDPOINTS } from '../constants';

// Types
export interface Application {
  id: string;
  userId: string;
  fromCountry: string;
  toCountry: string;
  visaType: string;
  purpose: string;
  status: string;
  submittedAt: string;
  updatedAt: string;
  documents: Document[];
  personalInfo: PersonalInfo;
  travelInfo: TravelInfo;
  emergencyContact: EmergencyContact;
  fees: ApplicationFees;
}

export interface Document {
  id: string;
  applicationId: string;
  type: string;
  filename: string;
  url: string;
  uploadedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  notes?: string;
}

export interface PersonalInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  nationality: string;
  passportNumber: string;
  passportExpiryDate: string;
  placeOfBirth: string;
  gender: string;
  maritalStatus: string;
  occupation: string;
  phoneNumber: string;
  email: string;
  address: Address;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface TravelInfo {
  purposeOfVisit: string;
  plannedArrivalDate: string;
  plannedDepartureDate: string;
  intendedStay: number;
  accommodationAddress?: string;
  invitationLetter?: boolean;
  previousVisits?: PreviousVisit[];
}

export interface PreviousVisit {
  country: string;
  visitDate: string;
  duration: number;
  purpose: string;
}

export interface EmergencyContact {
  name: string;
  relationship: string;
  phoneNumber: string;
  email: string;
  address: Address;
}

export interface ApplicationFees {
  baseFee: number;
  processingFee: number;
  serviceFee: number;
  total: number;
  currency: string;
}

export interface CreateApplicationRequest {
  fromCountry: string;
  toCountry: string;
  visaType: string;
  purpose: string;
  personalInfo: PersonalInfo;
  travelInfo: TravelInfo;
  emergencyContact: EmergencyContact;
}

export interface UpdateApplicationRequest extends Partial<CreateApplicationRequest> {
  id: string;
}

export interface DashboardStats {
  totalApplications: number;
  pendingApplications: number;
  approvedApplications: number;
  rejectedApplications: number;
  recentApplications: Application[];
}

// Application Service Class
class ApplicationService {
  // Create new application
  async createApplication(data: CreateApplicationRequest): Promise<ApiResponse<Application>> {
    return apiService.post<Application>(API_ENDPOINTS.APPLICATIONS, data);
  }

  // Get all applications for current user
  async getApplications(): Promise<ApiResponse<Application[]>> {
    return apiService.get<Application[]>(API_ENDPOINTS.APPLICATIONS);
  }

  // Get specific application by ID
  async getApplication(id: string): Promise<ApiResponse<Application>> {
    return apiService.get<Application>(`${API_ENDPOINTS.APPLICATIONS}/${id}`);
  }

  // Update application
  async updateApplication(data: UpdateApplicationRequest): Promise<ApiResponse<Application>> {
    return apiService.put<Application>(`${API_ENDPOINTS.APPLICATIONS}/${data.id}`, data);
  }

  // Delete application
  async deleteApplication(id: string): Promise<ApiResponse<{ message: string }>> {
    return apiService.delete<{ message: string }>(`${API_ENDPOINTS.APPLICATIONS}/${id}`);
  }

  // Submit application (change status from draft to submitted)
  async submitApplication(id: string): Promise<ApiResponse<Application>> {
    return apiService.post<Application>(`${API_ENDPOINTS.APPLICATIONS}/${id}/submit`);
  }

  // Upload document for application
  async uploadDocument(
    applicationId: string, 
    file: File, 
    documentType: string,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<Document>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', documentType);
    formData.append('applicationId', applicationId);

    return apiService.uploadFile<Document>(
      `${API_ENDPOINTS.DOCUMENTS}/upload`,
      file,
      onProgress
    );
  }

  // Get documents for application
  async getDocuments(applicationId: string): Promise<ApiResponse<Document[]>> {
    return apiService.get<Document[]>(`${API_ENDPOINTS.DOCUMENTS}?applicationId=${applicationId}`);
  }

  // Delete document
  async deleteDocument(documentId: string): Promise<ApiResponse<{ message: string }>> {
    return apiService.delete<{ message: string }>(`${API_ENDPOINTS.DOCUMENTS}/${documentId}`);
  }

  // Get dashboard statistics
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return apiService.get<DashboardStats>(API_ENDPOINTS.DASHBOARD.INDIVIDUAL);
  }

  // Search applications
  async searchApplications(query: string): Promise<ApiResponse<Application[]>> {
    return apiService.get<Application[]>(`${API_ENDPOINTS.APPLICATIONS}/search?q=${encodeURIComponent(query)}`);
  }

  // Get application status history
  async getStatusHistory(applicationId: string): Promise<ApiResponse<any[]>> {
    return apiService.get<any[]>(`${API_ENDPOINTS.APPLICATIONS}/${applicationId}/status-history`);
  }
}

export const applicationService = new ApplicationService();
